import { useEffect, useRef, useState } from 'react'

type GoogleCredentialResponse = {
  credential?: string
}

type GoogleIdentityServices = {
  accounts: {
    id: {
      initialize: (configuration: {
        client_id: string
        callback: (response: GoogleCredentialResponse) => void
        ux_mode?: 'popup'
      }) => void
      renderButton: (
        parent: HTMLElement,
        configuration: {
          type: 'standard'
          theme: 'outline'
          size: 'large'
          text: 'signin_with'
          shape: 'rectangular'
          logo_alignment: 'left'
          width: string
        }
      ) => void
    }
  }
}

declare global {
  interface Window {
    google?: GoogleIdentityServices
  }
}

const GOOGLE_SCRIPT_ID = 'google-identity-services'
const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

let googleScriptPromise: Promise<GoogleIdentityServices> | null = null
let initializedClientId: string | null = null
let activeCredentialHandler: ((credential: string) => void) | null = null

const getGoogleIdentityServices = () => {
  const google = window.google
  return google?.accounts?.id ? google : null
}

const loadGoogleIdentityServices = () => {
  const loaded = getGoogleIdentityServices()
  if (loaded) {
    return Promise.resolve(loaded)
  }

  if (googleScriptPromise) {
    return googleScriptPromise
  }

  const scriptPromise = new Promise<GoogleIdentityServices>((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_SCRIPT_ID) as HTMLScriptElement | null
    const script = existingScript ?? document.createElement('script')

    const handleLoad = () => {
      const google = getGoogleIdentityServices()
      if (google) {
        resolve(google)
      } else {
        script.remove()
        reject(new Error('Google Identity Services loaded without its browser API.'))
      }
    }
    const handleError = () => {
      script.remove()
      reject(new Error('Unable to load Google Identity Services.'))
    }

    script.addEventListener('load', handleLoad, { once: true })
    script.addEventListener('error', handleError, { once: true })

    if (!existingScript) {
      script.id = GOOGLE_SCRIPT_ID
      script.src = GOOGLE_SCRIPT_SRC
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    }
  })

  googleScriptPromise = scriptPromise.catch((error) => {
    googleScriptPromise = null
    throw error
  })
  return googleScriptPromise
}

type GoogleSignInButtonProps = {
  clientId: string
  disabled?: boolean
  onCredential: (credential: string) => void
}

export const GoogleSignInButton = ({
  clientId,
  disabled = false,
  onCredential
}: GoogleSignInButtonProps) => {
  const buttonContainerRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>('loading')

  useEffect(() => {
    let cancelled = false
    let registeredHandler: ((credential: string) => void) | null = null
    const container = buttonContainerRef.current

    void loadGoogleIdentityServices()
      .then((google) => {
        if (cancelled || !container) {
          return
        }

        registeredHandler = onCredential
        activeCredentialHandler = onCredential

        if (initializedClientId !== clientId) {
          google.accounts.id.initialize({
            client_id: clientId,
            ux_mode: 'popup',
            callback: (response) => {
              if (response.credential) {
                activeCredentialHandler?.(response.credential)
              }
            }
          })
          initializedClientId = clientId
        }

        container.replaceChildren()
        google.accounts.id.renderButton(container, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: Math.min(400, Math.max(240, container.clientWidth || 320)).toString()
        })
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('unavailable')
        }
      })

    return () => {
      cancelled = true
      if (activeCredentialHandler === registeredHandler) {
        activeCredentialHandler = null
      }
      container?.replaceChildren()
    }
  }, [clientId, onCredential])

  if (status === 'unavailable') {
    return (
      <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-800" role="status">
        Google sign-in is unavailable right now. Use your dashboard password instead.
      </p>
    )
  }

  return (
    <div
      className={`relative min-h-11 w-full ${disabled ? 'pointer-events-none opacity-60' : ''}`}
      aria-busy={status === 'loading'}
      aria-disabled={disabled}
    >
      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center rounded-md border border-border bg-background text-sm text-muted-foreground" role="status">
          Loading Google sign-in…
        </div>
      )}
      <div
        ref={buttonContainerRef}
        className={`flex w-full justify-center ${status === 'ready' ? '' : 'invisible'}`}
      />
    </div>
  )
}
